import type { ReactNode } from 'react';

/**
 * Les deux outils qu'aucun caractère ne sait dessiner.
 *
 * Tous les autres ont leur glyphe Unicode — `✎`, `▨`, `▭`, `⌫`. Unicode n'a ni
 * pipette ni flèche de curseur : `◔` ressemblait à un camembert, et `↖` à une
 * flèche de direction, pas à un pointeur de souris. Les deux sont donc tracés
 * ici, dans la même boîte de 24 que la hauteur de ligne des autres glyphes.
 *
 * Pas d'image à charger, pas de police d'icônes : du SVG en ligne, qui prend la
 * couleur du bouton par `currentColor` et suit donc l'état actif comme le ferait
 * un caractère.
 */

interface Props {
  readonly className?: string | undefined;
}

/**
 * Le pointeur de souris, dans sa forme canonique : pointe en haut à gauche,
 * une branche courte à droite et la queue qui part en bas.
 */
export function NavIcon({ className }: Props): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
      fill="currentColor"
    >
      <path d="M6 2.6 L6 19.8 L10.3 15.4 L13.1 21.4 L15.6 20.2 L12.8 14.4 L18.9 13.9 Z" />
    </svg>
  );
}

/**
 * La pipette.
 *
 * Trois primitives — poire, tube, pointe — assemblées à la verticale puis
 * basculées d'un quart de tour. C'est bien plus lisible qu'un tracé unique déjà
 * incliné, et la géométrie reste modifiable sans recalculer d'angles.
 */
export function PickerIcon({ className }: Props): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
      fill="currentColor"
    >
      <g transform="rotate(-45 12 12)">
        <rect x="9.3" y="2.2" width="5.4" height="5.6" rx="2.2" />
        <rect x="10.7" y="7.4" width="2.6" height="8.6" />
        <path d="M10.7 16 H13.3 L12 21.4 Z" />
      </g>
    </svg>
  );
}
